import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import TrialBanner from "../components/TrialBanner";
import { Search, Filter, TrendingUp, Star } from "lucide-react";
import { maybeStartFunderTourOnFirstVisit } from "../lib/tour";

const ticketK = (n) => `$${((n || 0) / 1000).toFixed(0)}K`;
const ticketRange = (d) => (!d.ticket_min && !d.ticket_max ? "Undisclosed" : d.ticket_min && d.ticket_max && Math.abs(d.ticket_min - d.ticket_max) < 1 ? ticketK(d.ticket_min) : `${ticketK(d.ticket_min)}–${ticketK(d.ticket_max)}`);

const SECTORS = ["", "Fintech", "Agritech", "Healthtech", "Cleantech", "Logistics", "E-commerce", "Manufacturing", "Edtech", "Real Estate", "Energy", "Infrastructure", "Agribusiness", "Hospitality"];
const COUNTRIES = ["", "Nigeria", "Kenya", "Ghana", "South Africa", "Rwanda", "Senegal", "Tanzania", "Uganda"];
const STAGES = ["", "Idea", "MVP", "Revenue", "Growth"];

const FUNDER_NAV = [
  {to:"/funder/terminal",label:"Terminal"},
  {to:"/funder/tutorial",label:"Tutorial"},
  {to:"/funder/pipeline",label:"Pipeline"},
  {to:"/funder/saved",label:"Saved"},
  {to:"/funder/analytics",label:"Analytics"},
  {to:"/funder/mandate",label:"Mandate"},
];

export default function FunderTerminal() {
  const nav = useNavigate();
  const [filters, setFilters] = useState({ sector:"", country:"", stage:"", min_score:0, q:"", kind:"" });
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const [saved, setSaved] = useState(new Set());
  // Seed from a device-local cache so the nav doesn't flicker (Analytics
  // briefly visible for individuals) while the mandate fetch resolves.
  const [investorType, setInvestorType] = useState(() => {
    try { return localStorage.getItem("clinq_investor_type"); } catch (_) { return null; }
  });
  const isIndividual = investorType === "individual";

  // Tailored views: individuals get a simpler, project-first terminal;
  // institutions keep the full mandate dashboard. Default tab follows the
  // funder's stated deal focus (quiz/mandate): projects → Projects tab,
  // businesses → Businesses tab; "both"/unset falls back to the old rule
  // (individuals project-first, institutions All).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/funder/mandate");
        const t = data?.investor_type === "individual" ? "individual" : "institution";
        setInvestorType(t);
        try { localStorage.setItem("clinq_investor_type", t); } catch (_) { /* ignore */ }
        const focus = data?.deal_focus;
        const defaultKind = focus === "projects" ? "project"
          : focus === "businesses" ? "business"
          : (t === "individual" ? "project" : "");
        if (defaultKind) {
          setFilters((f) => (f.kind === "" ? { ...f, kind: defaultKind } : f));
        }
      } catch (_) { setInvestorType("institution"); }
    })();
  }, []);

  const fetchSaved = useCallback(async () => {
    try { const { data } = await api.get("/funder/saved-ids"); setSaved(new Set(data.ids)); }
    catch (err) {
      if (process.env.NODE_ENV === "development") console.warn("[FunderTerminal] fetchSaved failed", err);
    }
  }, []);

  const toggleSave = async (id, e) => {
    e.stopPropagation();
    const isSaved = saved.has(id);
    const next = new Set(saved); next[isSaved ? "delete" : "add"](id); setSaved(next);
    try {
      if (isSaved) await api.delete(`/funder/sme/${id}/save`);
      else await api.post(`/funder/sme/${id}/save`);
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.warn("[FunderTerminal] toggleSave failed; resyncing", err);
      fetchSaved();
    }
  };

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k,v]) => { if(v) params[k]=v; });
    try {
      const { data } = await api.get("/funder/deals", { params });
      setDeals(data); setExpired(false);
    } catch (e) {
      if (e.response?.status === 402) { setExpired(true); setDeals([]); }
    }
    setLoading(false);
  }, [filters]);
  useEffect(() => { fetchDeals(); }, [fetchDeals]);
  useEffect(() => { fetchSaved(); }, [fetchSaved]);
  // First-time funders see the interactive product tour after the terminal loads.
  useEffect(() => {
    if (!loading && !expired) {
      maybeStartFunderTourOnFirstVisit();
    }
  }, [loading, expired]);

  return (
    <AppShell nav={isIndividual
      ? FUNDER_NAV.filter((n) => n.to !== "/funder/analytics")
      : FUNDER_NAV}>
      <TrialBanner />
      {expired && (
        <div data-testid="paywall-card" className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-8 text-center">
          <h3 className="text-xl font-bold text-red-800">Your free trial has ended</h3>
          <p className="mt-2 text-sm text-red-700">Upgrade to a paid plan to continue accessing the Terminal, SME profiles and VDR documents.</p>
          <button data-testid="paywall-upgrade-btn" onClick={()=>nav("/funder/billing")} className="mt-5 rounded-md bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700">
            Choose a plan
          </button>
        </div>
      )}
      <div className="rounded-2xl border border-white/10 bg-[#04302E] p-5 text-white sm:p-6" data-testid="terminal-header">
        <div className="flex flex-wrap items-baseline gap-3">
          <h1 className="text-xl font-bold sm:text-2xl" data-testid="terminal-title">
            {isIndividual ? "Investment Opportunities" : "Clinq Terminal"}
          </h1>
          <span className="rounded-full bg-[#7BD3CE]/20 px-2 py-0.5 text-xs font-semibold text-[#7BD3CE]">LIVE · {deals.length} opportunities</span>
        </div>
        <p className="mt-1 text-sm text-white/70">
          {isIndividual
            ? "Verified projects and businesses — see the full picture before you commit a shilling."
            : "Verified, mandate-matched deal flow from across Africa — businesses and capital projects."}
        </p>

        <div className="mt-4 inline-flex rounded-lg bg-white/10 p-1" data-testid="kind-toggle">
          {(isIndividual
            ? [["project", "Projects"], ["business", "Businesses"], ["", "All"]]
            : [["", "All"], ["business", "Businesses"], ["project", "Projects"]]
          ).map(([val, label]) => (
            <button key={val} data-testid={`kind-toggle-${val || "all"}`}
              onClick={() => setFilters({ ...filters, kind: val })}
              className={"rounded-md px-3 py-1.5 text-xs font-semibold transition-colors " +
                (filters.kind === val ? "bg-[#7BD3CE] text-[#04302E]" : "text-white/70 hover:text-white")}>
              {label}
            </button>
          ))}
        </div>

        <div className={"mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 " + (isIndividual ? "lg:grid-cols-3" : "lg:grid-cols-5")}>
          <div className="relative sm:col-span-2 lg:col-span-2">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
            <input data-testid="terminal-search" placeholder={isIndividual ? "Search opportunities…" : "Search SMEs…"} value={filters.q}
              onChange={e=>setFilters({...filters, q:e.target.value})}
              className="w-full rounded-lg bg-white/10 py-2 pl-9 pr-3 text-sm text-white placeholder:text-white/40 outline-none focus:bg-white/15"/>
          </div>
          <select data-testid="filter-sector" value={filters.sector} onChange={e=>setFilters({...filters,sector:e.target.value})} className={ftSel}>{SECTORS.map(s=><option key={s} value={s}>{s||"All sectors"}</option>)}</select>
          {!isIndividual && (
            <>
              <select data-testid="filter-country" value={filters.country} onChange={e=>setFilters({...filters,country:e.target.value})} className={ftSel}>{COUNTRIES.map(s=><option key={s} value={s}>{s||"All countries"}</option>)}</select>
              <select data-testid="filter-stage" value={filters.stage} onChange={e=>setFilters({...filters,stage:e.target.value})} className={ftSel}>{STAGES.map(s=><option key={s} value={s}>{s||"All stages"}</option>)}</select>
            </>
          )}
        </div>
        {!isIndividual && (
        <div className="mt-3 flex items-center gap-3 text-xs">
          <Filter className="h-3.5 w-3.5 text-[#7BD3CE]"/>
          <label className="flex items-center gap-2 text-white/70">Min score
            <input data-testid="filter-min-score" type="number" min={0} max={100} value={filters.min_score}
              onChange={e=>setFilters({...filters,min_score:+e.target.value})}
              className="w-16 rounded bg-white/10 px-2 py-1 text-white outline-none"/>
          </label>
        </div>
        )}
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-neutral-200 bg-white" data-testid="deal-table">
        {/* Desktop column headers — hidden on mobile, card view kicks in instead */}
        <div className="hidden grid-cols-12 bg-neutral-50 px-5 py-3 text-[10px] font-bold uppercase tracking-wider text-neutral-500 md:grid">
          <div className="col-span-4">{isIndividual ? "Opportunity" : "Company"}</div>
          <div className="col-span-2">{isIndividual ? "Location" : "Country"}</div>
          <div className="col-span-2">Sector</div><div className="col-span-1">Ticket</div>
          <div className="col-span-2 text-right">Compatibility</div>
          <div className="col-span-1 text-right">Save</div>
        </div>
        {loading ? <p className="p-8 text-center text-sm text-neutral-500">Loading…</p> :
          deals.length === 0 ? <p className="p-8 text-center text-sm text-neutral-500">No deals match your filters.</p> :
          deals.map(d => (
            <button key={d.company_id} onClick={()=>nav(`/funder/sme/${d.company_id}`)}
              data-testid={`deal-${d.company_id}`}
              className="block w-full border-t border-neutral-100 px-4 py-4 text-left text-sm transition-colors hover:bg-[#F4F8F8] md:grid md:grid-cols-12 md:items-center md:gap-2 md:px-5">
              {/* Mobile card layout */}
              <div className="flex items-start justify-between gap-3 md:hidden">
                <div className="min-w-0">
                  <p className="font-semibold text-neutral-900">
                    {d.company_name || "(unnamed)"}
                    {d.kind === "project" && (
                      <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 align-middle text-[10px] font-bold uppercase tracking-wider text-amber-700">Project</span>
                    )}
                  </p>
                  <p className="mt-0.5 text-xs text-neutral-500 line-clamp-2">{d.value_prop}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-neutral-600">
                    <span className="rounded-full bg-neutral-100 px-2 py-0.5">{d.kind === "project" ? (d.project_location || "—") : (d.country || "—")}</span>
                    <span className="rounded-full bg-neutral-100 px-2 py-0.5">{d.sector || "—"}</span>
                    <span className="rounded-full bg-neutral-100 px-2 py-0.5">{d.kind === "project" ? `Raising $${((d.ticket_min||0)/1000).toFixed(0)}K` : ticketRange(d)}</span>
                    {d.kind === "project" && d.project_capital_type && (
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-800">{d.project_capital_type}</span>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-2">
                  <div className="flex items-center gap-1.5 rounded-lg bg-[#0A8B85]/10 px-2 py-1">
                    <TrendingUp className="h-3.5 w-3.5 text-[#0A8B85]"/>
                    <span className="font-extrabold text-[#0A8B85]">{d.compatibility ?? 0}%</span>
                  </div>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e)=>toggleSave(d.company_id, e)}
                    onKeyDown={(e)=>{ if(e.key==="Enter"||e.key===" ") toggleSave(d.company_id, e); }}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[#0A8B85] hover:bg-[#0A8B85]/10">
                    <Star className={"h-4 w-4 " + (saved.has(d.company_id) ? "fill-current" : "")}/>
                  </span>
                </div>
              </div>

              {/* Desktop table layout */}
              <div className="hidden md:col-span-4 md:block">
                <p className="font-semibold text-neutral-900">
                  {d.company_name || "(unnamed)"}
                  {d.kind === "project" && (
                    <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 align-middle text-[10px] font-bold uppercase tracking-wider text-amber-700" data-testid={`project-badge-${d.company_id}`}>Project</span>
                  )}
                </p>
                <p className="text-xs text-neutral-500 line-clamp-1">{d.value_prop}</p>
              </div>
              <div className="hidden text-neutral-600 md:col-span-2 md:block">{d.kind === "project" ? (d.project_location || "—") : (d.country || "—")}</div>
              <div className="hidden text-neutral-600 md:col-span-2 md:block">{d.sector || "—"}</div>
              <div className="hidden text-xs text-neutral-600 md:col-span-1 md:block">{d.kind === "project" ? `$${((d.ticket_min||0)/1000).toFixed(0)}K` : ticketRange(d)}</div>
              <div className="hidden md:col-span-2 md:flex md:items-center md:justify-end md:gap-2">
                <span className="rounded-md bg-neutral-100 px-1.5 py-0.5 text-[10px] font-bold text-neutral-600" title="Capital Readiness Score out of 100">Readiness {d.readiness_score || 0}/100</span>
                <div className="flex items-center gap-1">
                  <TrendingUp className="h-3.5 w-3.5 text-[#0A8B85]"/>
                  <span className="font-extrabold text-[#0A8B85]" data-testid={`compat-${d.company_id}`}>{d.compatibility ?? 0}%</span>
                </div>
              </div>
              <div className="hidden md:col-span-1 md:block md:text-right">
                <span
                  role="button"
                  tabIndex={0}
                  data-testid={`save-${d.company_id}`}
                  onClick={(e)=>toggleSave(d.company_id, e)}
                  onKeyDown={(e)=>{ if(e.key==="Enter"||e.key===" ") toggleSave(d.company_id, e); }}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[#0A8B85] hover:bg-[#0A8B85]/10">
                  <Star className={"h-4 w-4 " + (saved.has(d.company_id) ? "fill-current" : "")}/>
                </span>
              </div>
            </button>
          ))}
      </div>
    </AppShell>
  );
}
const ftSel = "rounded-lg bg-white/10 px-3 py-2 text-sm text-white outline-none focus:bg-white/15";
export { FUNDER_NAV };
